import { Button, Stack, Tab, TableCell, TableRow, Tabs, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { config } from "../../../../config";
import { useApiServiceCallback } from "../../../../server/api/client";
import { EmailEngineSevice } from "../../../../server/api/email-engine-v2";
import { useSupabaseLoader } from "../../../../server/supabase/hooks";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import { useEnabledChannels } from "../../../channels/hooks/use-enabled-channels";
import { PaginatedTable } from "../../../generic/components/paginated-table/paginated-table";
import { SupabaseOptedOutService } from "../../server/supabase-opted-out-service";

import type { OptedOut } from "../../server/supabase-opted-out-service";
import type { FC } from "react";

type Channels = "personal_email" | "twilio" | "teams" | "whatsapp" | "work_email";

const tableFields: Record<Channels, { labels: string[]; values: (keyof OptedOut)[] }> = {
  personal_email: {
    labels: ["First Name", "Last Name", "Personal Email"],
    values: ["first_name", "last_name", "destination"],
  },
  twilio: {
    labels: ["First Name", "Last Name", "Phone Number", "Work Email"],
    values: ["first_name", "last_name", "destination", "work_email"],
  },
  teams: {
    labels: ["First Name", "Last Name", "Work Email"],
    values: ["first_name", "last_name", "work_email"],
  },
  whatsapp: {
    labels: ["First Name", "Last Name", "Phone Number", "Work Email"],
    values: ["first_name", "last_name", "destination", "work_email"],
  },
  work_email: {
    labels: ["First Name", "Last Name", "Work Email"],
    values: ["first_name", "last_name", "work_email"],
  },
};

const constructChannelHeader = (
  channel: Channels,
  optedOutData: OptedOut[],
  onSyncSuppressionList?: () => Promise<void>,
  syncing?: boolean,
): React.ReactNode => {
  switch (channel) {
    case "personal_email":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: "center", justifyContent: "space-between" }}
          >
            <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
              <ChannelIcon channel="personal_email" />
              <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
                Personal Email
              </Typography>
            </Stack>
            <Button
              data-analytics-id="opt-outs-sync-personal-email"
              variant="outlined"
              size="small"
              loading={syncing}
              onClick={() => void onSyncSuppressionList?.().catch(console.error)}
            >
              Sync Unreachables
            </Button>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving Personal Email
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people may have blocked messages from this platform, or their email address
                may be incorrect.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of any issues with delivering to Personal Email.
            </Typography>
          )}
        </Stack>
      );
    case "teams":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <ChannelIcon channel="teams" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
              Teams
            </Typography>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving Teams Communications
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people may have blocked messages from your Teams bot, or their email address
                may be incorrect. Please ask your HRIS or IT team to check these details.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of any issues with delivering Teams Communications.
            </Typography>
          )}
        </Stack>
      );
    case "twilio":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <ChannelIcon channel="twilio" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
              SMS
            </Typography>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving SMS
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people may have replied “STOP” to a previous SMS message from you, or their
                provider has blocked messages from this platform, or their phone number may be
                invalid.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of anyone who has opted out of SMS messages from you.
            </Typography>
          )}
        </Stack>
      );
    case "whatsapp":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <ChannelIcon channel="whatsapp" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
              WhatsApp
            </Typography>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving WhatsApp
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people may have replied “STOP” to a previous WhatsApp message from you, their
                phone number may be incorrect, or they don’t have a WhatsApp account.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of anyone who has opted out of WhatsApp messages from you.
            </Typography>
          )}
        </Stack>
      );
    case "work_email":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: "center", justifyContent: "space-between" }}
          >
            <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
              <ChannelIcon channel="work_email" />
              <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
                Work Email
              </Typography>
            </Stack>
            <Button
              data-analytics-id="opt-outs-sync-work-email"
              variant="outlined"
              size="small"
              loading={syncing}
              onClick={() => void onSyncSuppressionList?.().catch(console.error)}
            >
              Sync Unreachables
            </Button>
          </Stack>
          {optedOutData.length > 0 ? (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                Not receiving Work Email
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
                These people are not currently receiving Work Email Communications. They may have
                blocked emails from this platform, their email address may be incorrect, not set up
                for external emails, not fully set up when we first tried to contact them, or their
                inbox may be full. Please ask your HRIS or IT team to check these details.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of any issues with delivering to Work Email.
            </Typography>
          )}
        </Stack>
      );
    default:
      return <></>;
  }
};

const constructTable = (channel: Channels, optedOutData: OptedOut[]): React.ReactNode => {
  const channelId = config.channel[channel];
  const { labels, values } = tableFields[channel];

  return (
    <PaginatedTable
      config={{
        headers: labels,
      }}
      data={optedOutData.filter((d) => d.channel_id === channelId)}
      renderRow={(optOut: OptedOut) => (
        <TableRow data-testid="opted-out-grid-row">
          {values.map((value) => (
            <TableCell key={value}>{optOut[value]}</TableCell>
          ))}
        </TableRow>
      )}
    />
  );
};

const ALL_OPT_OUT_CHANNELS: { key: Channels; label: string }[] = [
  { key: "personal_email", label: "Personal Email" },
  { key: "twilio", label: "SMS" },
  { key: "teams", label: "Teams" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "work_email", label: "Work Email" },
];

export const OptOuts: FC = () => {
  const { data: enabledChannels, loading: enabledChannelsLoading } = useEnabledChannels();

  const { data: optedOutData, loading: optedOutLoading } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data, error } = await new SupabaseOptedOutService(supabase).getAll(account_id, {
        order: [{ column: "last_name" }],
      });
      if (error) throw error;
      if (!data) throw new Error("No opted-out data found");
      return data;
    },
    [],
  );

  const availableChannels = useMemo(() => {
    if (!enabledChannels) return [];
    return ALL_OPT_OUT_CHANNELS.filter(({ key }) => enabledChannels.includes(key));
  }, [enabledChannels]);

  const [displayedTab, setDisplayedTab] = useState<Channels>("twilio");
  const [syncing, setSyncing] = useState(false);

  const handleSyncSuppressionList = useApiServiceCallback(
    EmailEngineSevice,
    async ({ apiService }) => {
      setSyncing(true);
      try {
        const { error } = await apiService.updateSuppressionList();
        if (error) {
          toast.error("Failed to request opt-out sync. Please try again.");
        } else {
          toast.success("Sync requested. Please check back in a few minutes for updated records.");
        }
      } catch {
        toast.error("Failed to request opt-out sync. Please try again.");
      } finally {
        setSyncing(false);
      }
    },
    [],
  );

  // Fall back to the first available channel if the selected tab is not accessible
  const activeTab = useMemo(() => {
    const keys = availableChannels.map((c) => c.key);
    return keys.includes(displayedTab) ? displayedTab : keys[0];
  }, [availableChannels, displayedTab]);

  const channelSpecificData = useMemo(() => {
    if (optedOutLoading || !activeTab) return [];
    if (!optedOutData) return [];

    return optedOutData.filter((item) => item.channel_id === config.channel[activeTab]);
  }, [optedOutLoading, optedOutData, activeTab]);

  if (enabledChannelsLoading || !activeTab) return null;

  return (
    <Stack sx={{ gap: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
        Unreachable People
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
        These people will not receive Communications through specific platforms. Some don’t have
        this platform set up on their account. Others may have blocked or opted out of the platform,
        or their contact details may be incorrect.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
        SMS and WhatsApp opt-outs are recorded immediately. Email and Teams blocks or invalid
        details are only detected when you next send that person a Communication and their email
        provider or Microsoft Teams notifies us that delivery was blocked.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
        If their details are incorrect or invalid, you’ll need to correct them in their employee
        record.
      </Typography>
      {/* oxlint-disable-next-line typescript/no-unsafe-type-assertion */}
      <Tabs value={activeTab} onChange={(e, tab) => setDisplayedTab(tab as Channels)}>
        {availableChannels.map(({ key, label }) => (
          <Tab key={key} label={label} value={key} />
        ))}
      </Tabs>
      {constructChannelHeader(activeTab, channelSpecificData, handleSyncSuppressionList, syncing)}
      {channelSpecificData.length > 0 && constructTable(activeTab, channelSpecificData)}
    </Stack>
  );
};
